import React, { useState, useEffect, useMemo, useCallback, useRef, useLayoutEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Search, Play, Info, Bell } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { tvKey } from './tvKeys';
import { LOGO_URL, popNav, popOk, isKidProfile, isKidContent, preloadImg, tvImg, canExitViaBack } from './tvAssets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const isOrig = (it) => (it?.categories || []).includes('originals') || it?.category === 'originals';
const isRecentAdd = (it) => {
  if (!it) return false;
  const t = Date.parse(it.published_at || it.created_at || '');
  return !isNaN(t) && (Date.now() - t) < 15 * 86400000;
};
const TABS = [
  { id: 'accueil', label: 'Accueil' },
  { id: 'series', label: 'Séries' },
  { id: 'films', label: 'Films' },
  { id: 'jeux', label: 'Jeux' },
  { id: 'nouveautes', label: 'Nouveautés' },
  { id: 'malist', label: 'Ma liste' },
];
const P_W = 17; // portrait card width vw (4 vignettes par ligne : 1 paysage + 3 portraits, plein écran)
const L_W = 39; // focused landscape card width vw
const GAP = 1.5;
const STEP = P_W + 2.2; // pas horizontal d'une vignette (largeur portrait + espacement tv-row-wide)
const SHIFT = L_W - P_W; // décalage des vignettes à droite de la vignette dépliée
const HERO_H = 47.5; // bannière encore +14% vers le bas (façon Netflix)
const TYPE_LABEL = { fiction: 'Film', documentaire: 'Documentaire', series: 'Série', jeunesse: 'Jeunesse', game: 'Jeu' };

export function TvHome({ onOpenDetail, onOpenSearch, onOpenSettings, active, onExitRequest }) {
  const navigate = useNavigate();
  const { activeProfile } = useAuth();
  const kid = isKidProfile(activeProfile);
  const [data, setData] = useState({ cats: {}, order: [], all: [], top10: [], top10s: [] });
  const [cw, setCw] = useState([]);
  const [wl, setWl] = useState([]);
  const [recRows, setRecRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [tab, setTab] = useState('accueil');
  const [zone, setZone] = useState('hero'); // nav | hero | rows
  const [navIdx, setNavIdx] = useState(1);
  const [notifs, setNotifs] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifIdx, setNotifIdx] = useState(0);
  useEffect(() => {
    axios.get(`${API}/notifications`, { params: { platform: 'tv' }, withCredentials: true })
      .then(r => setNotifs(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);
  const notifUnread = notifs.filter(nn => !nn.read).length;
  const openNotifs = () => {
    setNotifIdx(0); setShowNotifs(true);
    if (notifUnread > 0) {
      axios.post(`${API}/notifications/mark-read`, {}, { withCredentials: true })
        .then(() => setNotifs(ns => ns.map(nn => ({ ...nn, read: true })))).catch(() => {});
    }
  };
  const [heroBtn, setHeroBtn] = useState(0);
  const [focus, setFocus] = useState({ row: 0, col: 0 });
  const [heroTrailer, setHeroTrailer] = useState(false);
  const [dwellId, setDwellId] = useState(null);
  const [top10Ids, setTop10Ids] = useState(null);
  const [newEpIds, setNewEpIds] = useState(null);
  const rowRefs = useRef([]);
  const scrollWrapRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);
  const trackRefs = useRef([]);
  const [rowView, setRowView] = useState({ x: 0, start: 0 });
  const setRowRef = useCallback((i, el) => { rowRefs.current[i] = el; }, []);
  const setTrackRef = useCallback((i, el) => { trackRefs.current[i] = el; }, []);
  const stopDwell = useCallback(() => setDwellId(null), []);
  const [vCol, setVCol] = useState(0); // colonne visuelle (peut atteindre n pendant la boucle)
  const [snap, setSnap] = useState(false); // true = repositionnement instantané sans animation
  const wrapTimer = useRef(null);
  const wrapRaf = useRef(null);
  const wrapping = useRef(false);
  const lapsRef = useRef(0); // tours de boucle effectués vers la droite (autorise le retour gauche)
  const holdRef = useRef(false); // touche directionnelle maintenue (répétition auto)
  const lastStepRef = useRef(0); // horodatage du dernier déplacement accepté
  const HOLD_STEP_MS = 300; // rythme de défilement quand la touche reste appuyée
  // double rAF : garantit que la frame de re-ancrage est peinte avant de lancer l'animation (TV lentes)
  const afterSnap = useCallback((fn) => {
    cancelAnimationFrame(wrapRaf.current);
    wrapRaf.current = requestAnimationFrame(() => { wrapRaf.current = requestAnimationFrame(fn); });
  }, []);
  const cancelWrap = useCallback(() => { clearTimeout(wrapTimer.current); cancelAnimationFrame(wrapRaf.current); wrapping.current = false; }, []);
  useEffect(() => () => { clearTimeout(wrapTimer.current); cancelAnimationFrame(wrapRaf.current); }, []);
  useEffect(() => {
    axios.get(`${API}/new-episode-badges`, { params: activeProfile ? { profile_id: activeProfile.id } : {}, withCredentials: true })
      .then(r => setNewEpIds(new Set(r.data?.content_ids || [])))
      .catch(() => {});
  }, [activeProfile]);
  useEffect(() => {
    axios.get(`${API}/top10`, { params: kid ? { kids: true } : {}, withCredentials: true })
      .then(r => setTop10Ids(new Set([...(r.data?.movies || []), ...(r.data?.series || [])].map(c => c.id))))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kid]);

  useEffect(() => {
    const load = async () => {
      try {
        const p = activeProfile ? { profile_id: activeProfile.id } : {};
        const [catRes, allRes, topRes, cwRes, wlRes, recRes] = await Promise.all([
          axios.get(`${API}/contents/categories`, { params: { slim: 1 }, withCredentials: true }),
          axios.get(`${API}/contents`, { params: { slim: 1 }, withCredentials: true }),
          axios.get(`${API}/top10`, { params: kid ? { kids: true } : {}, withCredentials: true }).catch(() => ({ data: {} })),
          axios.get(`${API}/continue-watching`, { params: p, withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API}/watchlist`, { params: p, withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API}/recommendations`, { params: p, withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        setData({ cats: catRes.data || {}, order: catRes.data?._order || [], all: allRes.data || [], top10: topRes.data?.movies || [], top10s: topRes.data?.series || [] });
        setCw(cwRes.data || []);
        setWl(wlRes.data || []);
        setRecRows(Array.isArray(recRes.data) ? recRes.data : []);
      } catch { /* ignore */ }
      setLoaded(true);
    };
    load();
    // Ma liste modifiée depuis la fiche (TvDetail) → rafraîchir immédiatement
    const onWl = () => {
      const p = activeProfile ? { profile_id: activeProfile.id } : {};
      axios.get(`${API}/watchlist`, { params: p, withCredentials: true }).then(r => setWl(r.data || [])).catch(() => {});
    };
    window.addEventListener('tv-watchlist-changed', onWl);
    return () => window.removeEventListener('tv-watchlist-changed', onWl);
  }, [activeProfile]);

  const refreshResume = useCallback(async () => {
    if (!activeProfile) return;
    try {
      const { data: d } = await axios.get(`${API}/continue-watching`, { params: { profile_id: activeProfile.id }, withCredentials: true });
      setCw(d || []);
    } catch { /* ignore */ }
  }, [activeProfile]);

  useEffect(() => { if (active && loaded) refreshResume(); }, [active, loaded, refreshResume]);

  useLayoutEffect(() => {
    if (zone !== 'rows') { setScrollY(0); return; }
    const el = rowRefs.current[focus.row];
    const wrap = scrollWrapRef.current;
    if (!el || !wrap) return;
    const wrapTop = wrap.getBoundingClientRect().top;
    const y = wrapTop + el.offsetTop + el.offsetHeight / 2 - window.innerHeight / 2;
    setScrollY(Math.max(0, Math.min(y, el.offsetTop)));
  }, [zone, focus.row, tab, loaded]);

  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') refreshResume(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshResume]);

  const rows = useMemo(() => {
    const { cats, order, all, top10, top10s } = data;
    const allowKid = c => !kid || isKidContent(c);
    const noGame = list => list.filter(c => c.type !== 'game');
    const r = [];
    if (tab === 'accueil') {
      const cwF = cw.filter(allowKid);
      if (cwF.length) r.push({ title: 'Reprendre la lecture', items: cwF, isResume: true });
      recRows.forEach(rec => {
        const items = (rec.items || []).filter(c => allowKid(c) && c.type !== 'game');
        if (items.length >= 3) r.push({ title: rec.row_title || `Parce que vous avez regardé ${rec.based_on_title}`, items });
      });
      if (kid) {
        const jeunesse = cats['jeunesse'] || [];
        const jeux = cats['jeux'] || [];
        const sj = jeunesse.filter(c => c.type === 'series');
        const oj = (cats['originals'] || []).filter(isKidContent);
        if (jeunesse.length) r.push({ title: 'Jeunesse', items: jeunesse });
        if (jeux.length) r.push({ title: 'Jeux', items: jeux });
        if (top10.length) r.push({ title: "Top 10 des films aujourd'hui", items: top10, isTop10: true });
        if (top10s.length) r.push({ title: "Top 10 des séries aujourd'hui", items: top10s, isTop10: true });
        if (sj.length) r.push({ title: 'Séries jeunesse', items: sj });
        if (oj.length) r.push({ title: 'Originals Maya+ jeunesse', items: oj });
      } else {
        if (top10.length) r.push({ title: "Top 10 des films aujourd'hui", items: top10, isTop10: true });
        if (top10s.length) r.push({ title: "Top 10 des séries aujourd'hui", items: top10s, isTop10: true });
        order.forEach(cat => {
          const items = noGame(cats[cat.id] || []);
          if (items.length) r.push({ title: cat.name, items });
        });
        if (r.length === 0 && all.length) r.push({ title: 'Catalogue', items: noGame(all) });
      }
    } else if (tab === 'series') {
      const items = all.filter(c => c.type === 'series' && allowKid(c));
      if (items.length) r.push({ title: 'Séries', items });
    } else if (tab === 'films') {
      const f = kid ? all.filter(c => c.type === 'jeunesse') : all.filter(c => c.type === 'fiction');
      if (f.length) r.push({ title: 'Films', items: f });
      if (!kid) {
        const d = all.filter(c => c.type === 'documentaire');
        if (d.length) r.push({ title: 'Documentaires', items: d });
      }
    } else if (tab === 'jeux') {
      const g = all.filter(c => c.type === 'game');
      if (g.length) r.push({ title: 'Jeux', items: g });
    } else if (tab === 'nouveautes') {
      const n = (cats['nouveautes'] || []).filter(c => allowKid(c) && c.type !== 'game');
      if (n.length) r.push({ title: 'Nouveautés', items: n });
    } else if (tab === 'malist') {
      const m = wl.filter(allowKid);
      if (m.length) r.push({ title: 'Ma liste', items: m });
    }
    return r.filter(row => row.items.length > 0);
  }, [data, cw, wl, recRows, tab, kid]);

  // Défilement horizontal : positions calculées ARITHMÉTIQUEMENT (zéro mesure DOM, zéro
  // re-layout par touche) pour les rangées classiques ; seul le Top 10 (chiffres à largeur
  // variable) reste mesuré au DOM
  useLayoutEffect(() => {
    if (zone !== 'rows' || !rows[focus.row]?.isTop10) { setRowView(v => (v.x === 0 && v.start === 0 ? v : { x: 0, start: 0 })); return; }
    const track = trackRefs.current[focus.row];
    const kids = track ? track.children : [];
    const el = kids[vCol];
    if (!el) { setRowView(v => (v.x === 0 && v.start === 0 ? v : { x: 0, start: 0 })); return; }
    const vw = window.innerWidth / 100;
    const last = kids[kids.length - 1];
    const maxX = Math.max(0, last.offsetLeft + last.offsetWidth - window.innerWidth * 0.945);
    const x = Math.min(el.offsetLeft, maxX);
    let start = 0;
    for (let i = 0; i < kids.length; i++) { if (kids[i].offsetLeft + kids[i].offsetWidth > x + vw * 0.5) { start = i; break; } }
    setRowView(v => (v.x === x && v.start === start ? v : { x, start }));
  }, [zone, focus.row, vCol, tab, loaded, rows]);

  const heroSeed = React.useRef(Math.random());
  const hero = useMemo(() => {
    const pool = rows.flatMap(r => r.items);
    const withTrailer = pool.filter(c => (c.tv_banner_url || c.banner_url) && c.trailer_url && c.type !== 'game');
    const cands = withTrailer.length ? withTrailer : pool.filter(c => (c.tv_banner_url || c.banner_url) && c.type !== 'game');
    if (!cands.length) return pool[0] || null;
    return cands[Math.floor(heroSeed.current * cands.length)];
  }, [rows]);

  const goWatch = useCallback((c, extra = '') => {
    try { sessionStorage.setItem('tv_resume', JSON.stringify({ id: c.id, title: c.title })); } catch { /* ignore */ }
    navigate(`/watch/${c.id}${extra}`);
  }, [navigate]);

  // Open detail passing the current trailer position so it keeps playing on the detail page
  const openWithTrailer = useCallback((item) => {
    const v = document.querySelector('[data-testid="tv-card-trailer"], [data-testid="tv-hero-trailer"]');
    onOpenDetail(item, v && !v.paused && v.currentTime > 0 ? v.currentTime : null);
  }, [onOpenDetail]);

  useEffect(() => { setFocus({ row: 0, col: 0 }); setZone('hero'); setHeroBtn(0); setVCol(0); setSnap(true); wrapping.current = false; lapsRef.current = 0; clearTimeout(wrapTimer.current); }, [tab]);

  // Hero trailer after 5s without any key press — only while on nav/hero, never over card trailers
  useEffect(() => {
    if (!active || !hero?.trailer_url || zone === 'rows') { setHeroTrailer(false); return; }
    let t;
    const arm = () => { clearTimeout(t); t = setTimeout(() => setHeroTrailer(true), 5000); };
    window.addEventListener('keydown', arm);
    arm();
    return () => { clearTimeout(t); window.removeEventListener('keydown', arm); setHeroTrailer(false); };
  }, [active, hero, zone]);

  const focusedItem = zone === 'rows' ? rows[focus.row]?.items?.[focus.col] : null;

  // Vue arithmétique de la rangée active (rangées classiques) : x en vw + 1re vignette visible
  const activeRowObj = zone === 'rows' ? rows[focus.row] : null;
  let liveView = { x: 0, start: 0 };
  if (activeRowObj) {
    if (activeRowObj.isTop10) liveView = rowView;
    else {
      const n = activeRowObj.items.length;
      const total = n >= 5 ? n + 5 : n;
      const totalW = (total - 1) * STEP + L_W;
      const x = Math.min(vCol * STEP, Math.max(0, totalW - 94.5));
      let start = 0;
      for (let i = 0; i < total; i++) {
        const right = i * STEP + (i === vCol ? L_W : P_W) + (i > vCol ? SHIFT : 0);
        if (right > x + 0.5) { start = i; break; }
      }
      liveView = { x, start };
    }
  }

  // Card trailer after 2s dwell on the focused landscape card (stops when detail opens / focus moves)
  useEffect(() => {
    setDwellId(null);
    if (!active || !focusedItem || !focusedItem.trailer_url || focusedItem.type === 'game') return;
    const t = setTimeout(() => setDwellId(focusedItem.id), 2000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedItem?.id, active]);

  // Preload landscape images around the focus so the portrait->landscape swap is instant
  useEffect(() => {
    if (!loaded) return;
    const land = it => it && (it.banner_url || it.thumbnail_url || it.poster_url);
    const targets = [];
    if (zone === 'rows') {
      const row = rows[focus.row];
      if (row) for (let c = Math.max(0, focus.col - 1); c <= focus.col + 4; c++) targets.push(row.items[c]);
      [rows[focus.row - 1], rows[focus.row + 1]].forEach(r => r && targets.push(...r.items.slice(0, 4)));
    } else if (rows[0]) {
      targets.push(...rows[0].items.slice(0, 5));
    }
    targets.forEach(it => preloadImg(tvImg(land(it), 800)));
  }, [loaded, zone, focus, rows]);

  useEffect(() => {
    if (!active) return;
    const onKey = (e) => {
      const k = tvKey(e);
      if (!k) return;
      e.preventDefault();
      // Vitesse de défilement maîtrisée : 1 appui = 1 pas ; touche maintenue = 1 pas toutes les HOLD_STEP_MS
      if (k === 'left' || k === 'right' || k === 'up' || k === 'down') {
        const now = Date.now();
        if (holdRef.current && now - lastStepRef.current < HOLD_STEP_MS) return;
        holdRef.current = true;
        lastStepRef.current = now;
      }
      if (showNotifs) {
        if (k === 'back') { popNav(); setShowNotifs(false); }
        else if (k === 'ok') {
          popOk();
          const sel = notifs[notifIdx];
          setShowNotifs(false);
          if (sel?.content_id) {
            // ouvre directement la fiche du contenu lié
            axios.get(`${API}/contents/${sel.content_id}`, { withCredentials: true })
              .then(r => { if (r.data?.id) onOpenDetail(r.data, null); }).catch(() => {});
          }
        }
        else if (k === 'down') { popNav(); setNotifIdx(i => Math.min(Math.max(notifs.length - 1, 0), i + 1)); }
        else if (k === 'up') { popNav(); setNotifIdx(i => Math.max(0, i - 1)); }
        return;
      }
      if (k === 'back') {
        // Fire TV (exigence Amazon) : Retour à la racine ouvre le dialogue « Quitter Maya+ ? »
        if (canExitViaBack && onExitRequest) { popNav(); onExitRequest(); }
        return; // LG/Samsung : le Retour ne quitte jamais l'app (quitter = bouton Accueil de la TV)
      }
      const maxNav = TABS.length + 2;
      if (zone === 'nav') {
        if (k === 'left') { if (navIdx > 0) popNav(); setNavIdx(i => Math.max(0, i - 1)); }
        else if (k === 'right') { if (navIdx < maxNav) popNav(); setNavIdx(i => Math.min(maxNav, i + 1)); }
        else if (k === 'down') { popNav(); setZone('hero'); }
        else if (k === 'ok') {
          popOk();
          if (navIdx === 0) onOpenSearch();
          else if (navIdx === TABS.length + 1) openNotifs();
          else if (navIdx === maxNav) onOpenSettings();
          else setTab(TABS[navIdx - 1].id);
        }
      } else if (zone === 'hero') {
        if (k === 'up') { popNav(); setZone('nav'); }
        else if (k === 'down') { if (rows.length) { popNav(); setZone('rows'); } }
        else if (k === 'left') { if (heroBtn > 0) popNav(); setHeroBtn(0); }
        else if (k === 'right') { if (heroBtn < 1) popNav(); setHeroBtn(1); }
        else if (k === 'ok' && hero) {
          popOk();
          if (heroBtn === 0) {
            if (hero.type === 'game') { if (hero.game_url) window.location.href = hero.game_url; }
            else goWatch(hero);
          } else openWithTrailer(hero);
        }
      } else {
        const row = rows[focus.row];
        const n = row ? row.items.length : 0;
        // boucle circulaire pour les catégories ; Top 10 : pas de boucle (fin de ligne bloquée à droite)
        const loop = !row?.isTop10 && n >= 5;
        if (k === 'up') {
          popNav(); cancelWrap(); lapsRef.current = 0;
          if (focus.row === 0) setZone('hero');
          else { setSnap(true); setVCol(0); setFocus(f => ({ row: f.row - 1, col: 0 })); }
        }
        else if (k === 'down') { if (focus.row < rows.length - 1) { popNav(); cancelWrap(); lapsRef.current = 0; setSnap(true); setVCol(0); setFocus(f => ({ row: f.row + 1, col: 0 })); } }
        else if (k === 'left') {
          if (n < 2 || wrapping.current) return;
          // butée : la première vignette marque le début — retour gauche possible uniquement après avoir bouclé
          if (focus.col === 0 && vCol < n && lapsRef.current === 0) return;
          popNav();
          clearTimeout(wrapTimer.current);
          if (focus.col > 0) { setSnap(false); setVCol(focus.col - 1); setFocus(f => ({ ...f, col: f.col - 1 })); }
          else {
            lapsRef.current = Math.max(0, lapsRef.current - 1);
            if (loop) {
              // boucle inverse : repositionnement invisible sur la copie de tête, puis glissement vers la dernière
              wrapping.current = true;
              setFocus(f => ({ ...f, col: n - 1 }));
              setSnap(true); setVCol(n);
              afterSnap(() => { setSnap(false); setVCol(n - 1); wrapping.current = false; });
            } else { setSnap(true); setVCol(n - 1); setFocus(f => ({ ...f, col: n - 1 })); }
          }
        }
        else if (k === 'right') {
          if (n < 2 || wrapping.current) return;
          // Top 10 : la dernière vignette reste au bord droit, butée (la sélection se déplace via le clamp)
          if (row?.isTop10 && focus.col >= n - 1) return;
          popNav();
          clearTimeout(wrapTimer.current);
          if (vCol >= n) {
            // boucle en cours (touche maintenue) : ré-ancrage invisible au début puis on continue sans pause
            wrapping.current = true;
            setFocus(f => ({ ...f, col: 1 % n }));
            setSnap(true); setVCol(0);
            afterSnap(() => { setSnap(false); setVCol(1 % n); wrapping.current = false; });
          }
          else if (focus.col < n - 1) { setSnap(false); setVCol(focus.col + 1); setFocus(f => ({ ...f, col: f.col + 1 })); }
          else if (loop) {
            // boucle : glisse vers la copie de la première vignette ; ré-ancrage invisible une fois arrivé
            lapsRef.current += 1;
            setFocus(f => ({ ...f, col: 0 }));
            setSnap(false); setVCol(n);
            wrapTimer.current = setTimeout(() => { setSnap(true); setVCol(0); }, 260);
          } else { lapsRef.current += 1; setSnap(true); setVCol(0); setFocus(f => ({ ...f, col: 0 })); }
        }
        else if (k === 'ok' && focusedItem) {
          popOk();
          openWithTrailer(focusedItem); // toujours passer par la fiche, même depuis « Reprendre la lecture »
        }
      }
    };
    const onKeyUp = () => { holdRef.current = false; };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKeyUp);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('keyup', onKeyUp); };
  });

  if (!loaded) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <div style={{ width: '3vw', height: '3vw', border: '0.3vw solid rgba(233,100,26,0.2)', borderTopColor: '#E9641A', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
    </div>;
  }

  const chip = (focused, activeTab) => ({
    padding: '0.55vw 1.55vw', borderRadius: '2.4vw', fontSize: '1.26vw', fontWeight: 700, flex: 'none', cursor: 'pointer',
    background: focused ? '#E9641A' : activeTab ? 'rgba(255,255,255,0.92)' : 'transparent',
    color: focused ? '#fff' : activeTab ? '#0b0612' : 'rgba(255,255,255,0.75)',
    transition: 'background-color 0.15s, color 0.15s',
  });

  return (
    <div className="tv-fade-in" style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} data-testid="tv-home">
      {/* Top navigation */}
      <div style={{ flex: 'none', display: 'flex', alignItems: 'center', padding: '1.5vw 4vw 1.5vw', zIndex: 5 }} data-testid="tv-nav">
        <div className="tv-btn" onClick={onOpenSearch}
          style={{ ...chip(zone === 'nav' && navIdx === 0, false), padding: '0.55vw 1.1vw', marginRight: '2vw' }} data-testid="tv-search-btn">
          <Search style={{ width: '1.45vw', height: '1.45vw', display: 'block' }} />
        </div>
        {TABS.map((t, i) => (
          <div key={t.id} className="tv-btn" onClick={() => setTab(t.id)}
            style={{ ...chip(zone === 'nav' && navIdx === i + 1, tab === t.id), marginRight: '2vw' }} data-testid={`tv-tab-${t.id}`}>
            {t.label}
          </div>
        ))}
        <div style={{ flex: 1 }} />
        <div className="tv-btn" onClick={openNotifs}
          style={{ ...chip(zone === 'nav' && navIdx === TABS.length + 1, false), padding: '0.55vw 1.1vw', position: 'relative', marginRight: '1.4vw' }}
          data-testid="tv-bell-btn">
          <Bell style={{ width: '1.45vw', height: '1.45vw', display: 'block' }} />
          {notifUnread > 0 && (
            <span data-testid="tv-bell-badge" style={{ position: 'absolute', top: '-0.15vw', right: '-0.15vw', minWidth: '1.05vw', height: '1.05vw',
              borderRadius: '0.55vw', background: '#E9641A', color: '#fff', fontSize: '0.65vw', fontWeight: 800,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 0.2vw', border: '0.12vw solid #0b0612' }}>
              {notifUnread > 9 ? '9+' : notifUnread}
            </span>
          )}
        </div>
        <div className="tv-btn" onClick={onOpenSettings}
          style={{ width: '2.2vw', height: '2.2vw', borderRadius: '50%', overflow: 'hidden', flex: 'none', cursor: 'pointer',
            background: activeProfile?.avatar_color || '#E9641A', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1vw', fontWeight: 800, color: '#fff',
            outline: zone === 'nav' && navIdx === TABS.length + 2 ? '0.18vw solid #E9641A' : 'none', outlineOffset: '0.15vw' }}
          data-testid="tv-settings-btn">
          {activeProfile?.avatar_url
            ? <img src={activeProfile.avatar_url} alt={activeProfile?.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : (activeProfile?.name || '?')[0].toUpperCase()}
        </div>
        <img src={LOGO_URL} alt="Maya+" style={{ height: '1.8vw', marginLeft: '0.6vw' }} />
      </div>

      {/* Panneau notifications (cloche) */}
      {showNotifs && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 40, background: 'rgba(5,2,10,0.78)' }} data-testid="tv-notif-panel">
          <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: '34vw', background: '#14091f',
            borderLeft: '0.1vw solid rgba(255,255,255,0.08)', padding: '2vw 1.8vw', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1.4vw' }}>
              <Bell style={{ width: '1.5vw', height: '1.5vw', color: '#E9641A', marginRight: '0.7vw' }} />
              <span style={{ fontSize: '1.6vw', fontWeight: 800, color: '#fff' }}>Notifications</span>
            </div>
            {notifs.length === 0 && (
              <p style={{ fontSize: '1.1vw', color: 'rgba(255,255,255,0.45)' }} data-testid="tv-notif-empty">Aucune notification pour le moment.</p>
            )}
            <div style={{ transform: `translateY(${-Math.max(0, notifIdx - 3) * 7.2}vw)`, transition: 'transform 0.2s ease' }}>
              {notifs.map((nn, i) => (
                <div key={nn.id || i} data-testid={`tv-notif-item-${i}`}
                  style={{ display: 'flex', alignItems: 'center', padding: '0.9vw 1vw', marginBottom: '0.7vw', borderRadius: '0.6vw', minHeight: '5.6vw',
                    background: i === notifIdx ? 'rgba(233,100,26,0.16)' : 'rgba(255,255,255,0.04)',
                    border: i === notifIdx ? '0.14vw solid #E9641A' : '0.14vw solid transparent' }}>
                  {nn.content_thumb && (
                    <img src={tvImg(nn.content_thumb, 300)} alt="" data-testid={`tv-notif-thumb-${i}`}
                      style={{ width: '7.6vw', height: '4.3vw', objectFit: 'cover', borderRadius: '0.4vw', marginRight: '1vw', flex: 'none' }} />
                  )}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <p style={{ fontSize: '1.05vw', fontWeight: 800, color: '#fff', marginBottom: '0.3vw' }}>{nn.title}</p>
                    <p style={{ fontSize: '0.95vw', color: 'rgba(255,255,255,0.7)', lineHeight: 1.45, display: '-webkit-box', WebkitLineClamp: nn.content_thumb ? 2 : 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{nn.message}</p>
                    <p style={{ fontSize: '0.75vw', color: 'rgba(255,255,255,0.35)', marginTop: '0.35vw' }}>
                      {nn.created_at ? new Date(nn.created_at).toLocaleDateString('fr-FR') : ''}
                      {nn.content_id && i === notifIdx && <span style={{ color: '#E9641A', fontWeight: 800, marginLeft: '0.8vw' }}>OK pour voir la fiche</span>}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <p style={{ position: 'absolute', bottom: '1.2vw', left: '1.8vw', fontSize: '0.85vw', color: 'rgba(255,255,255,0.35)' }}>Retour ou OK pour fermer</p>
          </div>
        </div>
      )}

      {/* Scrollable content */}
      <div ref={scrollWrapRef} style={{ flex: 1, overflow: 'hidden' }}>
        <div style={{ transform: `translate3d(0, ${-scrollY}px, 0)`, transition: 'transform 0.28s ease-out', willChange: 'transform' }}>
          {/* Hero with white frame */}
          <div style={{ height: `${HERO_H - 2.5}vw`, margin: '0.4vw 4vw 2vw', position: 'relative', borderRadius: '1.2vw', overflow: 'hidden',
            border: '0.14vw solid rgba(255,255,255,0.85)', background: '#0b0612' }} data-testid="tv-hero">
            {hero && (
              <>
                <img src={tvImg(hero.tv_banner_url || hero.banner_url || hero.thumbnail_url, 1280)} alt="" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', borderRadius: '1.1vw' }} />
                {heroTrailer && hero.trailer_url && (
                  <video src={hero.trailer_url} autoPlay playsInline
                    onLoadedMetadata={e => { const ts = parseFloat(hero.trailer_trim_start) || 0; if (ts > 0) e.target.currentTime = ts; }}
                    onTimeUpdate={e => { const te = parseFloat(hero.trailer_trim_end) || 0; if (te > 0 && e.target.currentTime >= te) { e.target.pause(); setHeroTrailer(false); } }}
                    onEnded={() => setHeroTrailer(false)} onError={() => setHeroTrailer(false)}
                    className="tv-fade-in" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center top', borderRadius: '1.1vw' }}
                    data-testid="tv-hero-trailer" />
                )}
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'linear-gradient(90deg, rgba(11,6,18,0.92) 8%, rgba(11,6,18,0.45) 42%, transparent 68%), linear-gradient(0deg, rgba(11,6,18,0.85) 0%, transparent 38%)' }} />
                <div style={{ position: 'absolute', left: '2.5vw', bottom: '2vw', maxWidth: '43vw' }}>
                  {hero.logo_url
                    ? <img src={hero.logo_url} alt={hero.title} style={{ maxHeight: '8.5vw', maxWidth: '30vw', objectFit: 'contain', marginBottom: '0.9vw' }} />
                    : <h2 style={{ fontSize: '3.3vw', fontWeight: 800, lineHeight: 1.05, marginBottom: '0.8vw' }} data-testid="tv-hero-title">{hero.title}</h2>}
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.8vw', fontSize: '1.2vw', color: 'rgba(255,255,255,0.8)' }}>
                    <span style={{ fontWeight: 700, marginRight: '0.8vw' }}>{TYPE_LABEL[hero.type] || ''}</span>
                    {hero.year && <span style={{ marginRight: '0.8vw' }}>· {hero.year}</span>}
                    {hero.duration && <span style={{ marginRight: '0.8vw' }}>· {hero.duration}</span>}
                    {hero.age_rating && <span>· {hero.age_rating}</span>}
                  </div>
                  <p style={{ fontSize: '1.25vw', color: 'rgba(255,255,255,0.75)', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', marginBottom: '1.2vw' }}>
                    {hero.description}
                  </p>
                  <div style={{ display: 'flex' }}>
                    <div className="tv-btn" onClick={() => goWatch(hero)}
                      style={{ display: 'flex', alignItems: 'center', marginRight: '1vw', padding: '0.7vw 2.1vw', borderRadius: '2.2vw', fontSize: '1.4vw', fontWeight: 800,
                        background: zone === 'hero' && heroBtn === 0 ? '#fff' : 'rgba(255,255,255,0.25)',
                        color: zone === 'hero' && heroBtn === 0 ? '#0b0612' : '#fff' }}
                      data-testid="tv-hero-play">
                      <Play style={{ width: '1.35vw', height: '1.35vw', marginRight: '0.65vw', fill: 'currentColor' }} />
                      Lecture
                    </div>
                    <div className="tv-btn" onClick={() => openWithTrailer(hero)}
                      style={{ display: 'flex', alignItems: 'center', padding: '0.7vw 2.1vw', borderRadius: '2.2vw', fontSize: '1.4vw', fontWeight: 700,
                        background: zone === 'hero' && heroBtn === 1 ? '#fff' : 'rgba(255,255,255,0.25)',
                        color: zone === 'hero' && heroBtn === 1 ? '#0b0612' : '#fff' }}
                      data-testid="tv-hero-info">
                      <Info style={{ width: '1.35vw', height: '1.35vw', marginRight: '0.65vw' }} />
                      Plus d'infos
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Rows */}
          <div style={{ paddingLeft: '4vw' }}>
            {rows.map((row, ri) => {
              const isActive = zone === 'rows' && ri === focus.row;
              return (
                <TvRow key={row.title} row={row} ri={ri} isActive={isActive}
                  dim={zone === 'rows' && Math.abs(ri - focus.row) > 1}
                  focusCol={isActive ? focus.col : 0}
                  vCol={isActive ? vCol : 0}
                  snap={isActive ? snap : true}
                  rowX={isActive ? liveView.x : 0}
                  visStart={isActive ? liveView.start : 0}
                  dwellId={isActive ? dwellId : null}
                  top10Ids={top10Ids} newEpIds={newEpIds}
                  setRowRef={setRowRef} setTrackRef={setTrackRef} stopDwell={stopDwell} onOpen={openWithTrailer} />
              );
            })}
            {rows.length === 0 && (
              <p style={{ fontSize: '1.2vw', color: 'rgba(255,255,255,0.4)', padding: '3vw 0' }} data-testid="tv-empty-tab">
                {tab === 'malist' ? 'Votre liste est vide' : 'Aucun contenu dans cette section'}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Rangée mémoïsée : seule la rangée active se re-rend à chaque appui de touche.
// Rangées classiques : vignettes en POSITION ABSOLUE (offsets arithmétiques en vw) +
// fenêtrage DOM (~13 vignettes montées max) + décalage par transform GPU → zéro re-layout
// de la rangée par touche, indispensable pour la fluidité sur les CPU de vieilles TV.
// Top 10 : flux flex mesuré au DOM (chiffres géants à largeur variable), ≤ 10 vignettes.
const TvRow = React.memo(function TvRow({ row, ri, isActive, dim, focusCol, vCol, snap, rowX, visStart, dwellId, top10Ids, newEpIds, setRowRef, setTrackRef, stopDwell, onOpen }) {
  const focusedItem = isActive ? row.items[focusCol] : null;
  const n = row.items.length;
  const loop = !row.isTop10 && n >= 5;
  const total = loop ? n + 5 : n;

  const card = (item, fc, showImg) => (
    <div className="tv-card" style={{ width: `${fc ? L_W : P_W}vw`, height: '24vw', position: 'relative', zIndex: 1,
      borderRadius: '0.6vw', overflow: 'hidden', background: '#170b26', cursor: 'pointer',
      border: fc ? '0.16vw solid #fff' : '0.16vw solid transparent' }}
      onClick={() => onOpen && onOpen(item)}
      data-testid={`tv-card-${item.id}`}>
      {showImg && (
        <img src={fc ? tvImg(item.banner_url || item.thumbnail_url || item.poster_url, 800) : tvImg(item.poster_url || item.thumbnail_url, 400)} alt={item.title}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" decoding="async" />
      )}
      {fc && dwellId === item.id && item.trailer_url && (
        <video src={item.trailer_url} autoPlay playsInline
          onLoadedMetadata={e => { const ts = parseFloat(item.trailer_trim_start) || 0; if (ts > 0) e.target.currentTime = ts; }}
          onTimeUpdate={e => { const te = parseFloat(item.trailer_trim_end) || 0; if (te > 0 && e.target.currentTime >= te) { e.target.pause(); stopDwell(); } }}
          onEnded={stopDwell} onError={stopDwell}
          className="tv-fade-in" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          data-testid="tv-card-trailer" />
      )}
      {row.isResume && item.progress > 0 && (
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '0.35vw', background: 'rgba(255,255,255,0.25)' }}>
          <div style={{ height: '100%', width: `${item.progress}%`, background: '#E9641A' }} />
        </div>
      )}
      {isOrig(item) && (
        <img src={LOGO_URL} alt="Maya+" data-testid={`tv-original-badge-${item.id}`}
          style={{ position: 'absolute', zIndex: 2, height: '0.9vw', width: 'auto', pointerEvents: 'none',
            filter: 'drop-shadow(0 0.1vw 0.3vw rgba(0,0,0,0.9))',
            ...(fc ? { top: '0.5vw', right: '0.6vw' } : { top: '0.5vw', left: '50%', transform: 'translateX(-50%)' }) }} />
      )}
      {!row.isTop10 && top10Ids?.has(item.id) && (
        <span data-testid={`tv-top10-badge-${item.id}`}
          style={{ position: 'absolute', top: 0, left: '0.6vw', zIndex: 2, display: 'flex', flexDirection: 'column', alignItems: 'center',
            background: '#E9641A', color: '#fff', borderRadius: '0 0 0.25vw 0.25vw', padding: '0.18vw 0.32vw 0.26vw', lineHeight: 1,
            boxShadow: '0 0.1vw 0.4vw rgba(0,0,0,0.5)', pointerEvents: 'none' }}>
          <span style={{ fontSize: '0.5vw', fontWeight: 800, letterSpacing: '0.05em' }}>TOP</span>
          <span style={{ fontSize: '0.8vw', fontWeight: 900, marginTop: '0.06vw' }}>10</span>
        </span>
      )}
      {newEpIds?.has(item.id) && (
        <span data-testid={`tv-new-episode-badge-${item.id}`}
          style={{ position: 'absolute', bottom: row.isResume ? '0.6vw' : '0.35vw', left: '50%', transform: 'translateX(-50%)', zIndex: 2,
            background: '#E9641A', color: '#fff', borderRadius: '0.2vw', padding: '0.18vw 0.4vw', lineHeight: 1,
            fontSize: '0.55vw', fontWeight: 800, letterSpacing: '0.03em', whiteSpace: 'nowrap',
            boxShadow: '0 0.1vw 0.4vw rgba(0,0,0,0.5)', pointerEvents: 'none' }}>
          NOUVEAUX ÉPISODES
        </span>
      )}
      {!newEpIds?.has(item.id) && isRecentAdd(item) && (
        <span data-testid={`tv-recent-badge-${item.id}`}
          style={{ position: 'absolute', bottom: row.isResume ? '0.6vw' : '0.35vw', left: '50%', transform: 'translateX(-50%)', zIndex: 2,
            background: '#7C3AED', color: '#fff', borderRadius: '0.2vw', padding: '0.18vw 0.4vw', lineHeight: 1,
            fontSize: '0.55vw', fontWeight: 800, letterSpacing: '0.03em', whiteSpace: 'nowrap',
            boxShadow: '0 0.1vw 0.4vw rgba(0,0,0,0.5)', pointerEvents: 'none' }}>
          AJOUT RÉCENT
        </span>
      )}
    </div>
  );

  return (
    <div ref={el => setRowRef(ri, el)} style={{ marginBottom: '1.4vw', opacity: dim ? 0.3 : 1, transition: 'opacity 0.3s' }}>
      <h3 style={{ fontSize: '1.8vw', fontWeight: 800, color: isActive ? '#fff' : 'rgba(255,255,255,0.55)', marginBottom: '0.6vw' }}>{row.title}</h3>
      {row.isTop10 ? (
        <div className="tv-row-track" ref={el => setTrackRef(ri, el)}
          style={{ alignItems: 'flex-end', transform: `translate3d(${isActive ? -rowX : 0}px, 0, 0)`, transition: snap ? 'none' : undefined }}>
          {(isActive ? row.items : row.items.slice(0, 10)).map((item, ci) => {
            const fc = isActive && ci === vCol;
            const showImg = !isActive || ci < 2 || (ci >= vCol - 4 && ci <= vCol + 8);
            return (
              <div key={item.id} style={{ position: 'relative', flex: 'none', display: 'flex', alignItems: 'flex-end', visibility: isActive && ci < visStart ? 'hidden' : 'visible' }}>
                <span style={{ fontSize: '13vw', fontWeight: 900, color: '#E9641A', lineHeight: 0.75, marginRight: '-1vw', zIndex: 0,
                  textShadow: '0 0.2vw 1vw rgba(0,0,0,0.6)', fontFamily: 'Arial, sans-serif', letterSpacing: '-0.06em' }}>
                  {ci + 1}
                </span>
                {card(item, fc, showImg)}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="tv-row-abs" style={{ transform: `translate3d(${isActive ? -rowX : 0}vw, 0, 0)`, transition: snap ? 'none' : undefined }}>
          {Array.from({ length: total }, (_, ci) => {
            // fenêtre de montage : rangée active → autour du focus ; rangée inactive → 6 premières
            if (isActive ? (ci < vCol - 2 || ci > vCol + 8) : ci >= 6) return null;
            const item = row.items[ci % n];
            const fc = isActive && ci === vCol;
            return (
              <div key={ci >= n ? `${item.id}-loop` : item.id}
                style={{ position: 'absolute', left: `${ci * STEP}vw`, top: '1vw',
                  visibility: isActive && ci < visStart ? 'hidden' : 'visible',
                  transform: isActive && ci > vCol ? `translateX(${SHIFT}vw)` : 'translateX(0)',
                  transition: snap ? 'none' : 'transform 0.22s ease-out' }}>
                {card(item, fc, true)}
              </div>
            );
          })}
        </div>
      )}
      {isActive && focusedItem && (
        <div style={{ marginTop: '0.9vw', maxWidth: '58vw' }} data-testid="tv-row-info">
          <div style={{ display: 'flex', fontSize: '1.35vw', fontWeight: 700, color: 'rgba(255,255,255,0.9)', marginBottom: '0.5vw', flexWrap: 'wrap' }}>
            <span style={{ marginRight: '0.8vw' }}>{TYPE_LABEL[focusedItem.type] || ''}</span>
            {focusedItem.year && <span style={{ marginRight: '0.8vw' }}>· {focusedItem.year}</span>}
            {focusedItem.duration && <span style={{ marginRight: '0.8vw' }}>· {focusedItem.duration}</span>}
            {focusedItem.age_rating && <span>· {focusedItem.age_rating}</span>}
            {focusedItem.remaining_label && <span style={{ color: '#E9641A' }}>· {focusedItem.remaining_label}</span>}
          </div>
          <p style={{ fontSize: '1.35vw', color: 'rgba(255,255,255,0.75)', lineHeight: 1.55, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {focusedItem.description}
          </p>
        </div>
      )}
    </div>
  );
});
